/**
 * Payroll Calculation Utilities for Zambian Schools
 * Handles NAPSA, PAYE, NHIMA and other statutory deductions
 * 
 * Zambia Tax Rates (2025 defaults, configurable on backend):
 * - NAPSA: 10% of gross salary split 5% employee / 5% employer
 *   capped at ZMW 1,708.20 employee and ZMW 3,416.40 total monthly
 * - NHIMA: 1% employee + 1% employer on basic salary
 * - PAYE: Progressive tax brackets (0%, 20%, 30%, 37%)
 */

// ===== Constants for Zambia Payroll =====

export const NAPSA_CONFIG = {
  employeeRate: 0.05,  // 5% employee contribution
  employerRate: 0.05,  // 5% employer contribution
  monthlyCeiling: 34164, // 2025 ceiling base amount
  maxEmployeeContribution: 1708.2,
  maxTotalContribution: 3416.4,
};

export const NHIMA_CONFIG = {
  employeeRate: 0.01, // 1% employee contribution
  employerRate: 0.01, // 1% employer contribution
};

export const PAYE_BRACKETS = [
  { min: 0, max: 5100, rate: 0 },             // 0% on first ZMW 5,100
  { min: 5100, max: 7100, rate: 0.20 },       // 20% on next ZMW 2,000
  { min: 7100, max: 9200, rate: 0.30 },       // 30% on next ZMW 2,100
  { min: 9200, max: Infinity, rate: 0.37 },   // 37% above ZMW 9,200
];

// ===== Type Definitions =====

export interface SalaryComponents {
  baseSalary: number;
  housingAllowance: number;
  transportAllowance: number;
  responsibilityAllowance: number;
  otherAllowances: number;
}

export interface DeductionComponents {
  daysAbsentDeduction: number;
  advanceSalaryRepayment: number;
  loanRepayment: number;
  rentDeduction: number;
  schoolFeesDeduction: number;
  personalLevy: number;
  otherDeductions: number;
  manualNapsa?: number;
  manualPaye?: number;
  manualNhima?: number;
}

export interface TaxBreakdown {
  employeeNapsa: number;
  employerNapsa: number;
  totalNapsa: number;
  paye: number;
  employeeNhima: number;
  employerNhima: number;
  totalNhima: number;
  personalLevy: number;
  totalStatutory: number;
}

export interface PayrollCalculationResult {
  // Earnings
  baseSalary: number;
  housingAllowance: number;
  transportAllowance: number;
  responsibilityAllowance: number;
  otherAllowances: number;
  grossSalary: number;

  // Deductions
  daysAbsentDeduction: number;
  advanceSalaryRepayment: number;
  loanRepayment: number;
  napsaDeduction: number;
  employeeNapsa: number;
  employerNapsa: number;
  totalNapsa: number;
  payeDeduction: number;
  nhimaDeduction: number;
  employeeNhima: number;
  employerNhima: number;
  totalNhima: number;
  personalLevy: number;
  rentDeduction: number;
  schoolFeesDeduction: number;
  otherDeductions: number;
  totalDeductions: number;

  // Net
  netSalary: number;

  // Tax breakdown
  taxBreakdown: TaxBreakdown;

  // Leave info
  leaveDaysTaken: number;
  leaveDaysRemaining: number;
}

// ===== Calculation Functions =====

/**
 * Calculate NAPSA deduction
 * Capped at ZMW 1,342 (ceiling ZMW 26,840)
 */
export function calculateNapsa(grossSalary: number) {
  const pensionableEarnings = Math.max(0, Math.min(grossSalary, NAPSA_CONFIG.monthlyCeiling));
  const employeeContribution = Math.min(
    pensionableEarnings * NAPSA_CONFIG.employeeRate,
    NAPSA_CONFIG.maxEmployeeContribution
  );
  const employerContribution = Math.min(
    pensionableEarnings * NAPSA_CONFIG.employerRate,
    NAPSA_CONFIG.maxTotalContribution - employeeContribution
  );

  return {
    employeeContribution,
    employerContribution,
    totalContribution: employeeContribution + employerContribution,
    pensionableEarnings
  };
}

/**
 * Calculate NHIMA deduction (1% of basic salary)
 */
export function calculateNhima(baseSalary: number) {
  const safeBaseSalary = Math.max(0, baseSalary);
  const employeeContribution = safeBaseSalary * NHIMA_CONFIG.employeeRate;
  const employerContribution = safeBaseSalary * NHIMA_CONFIG.employerRate;
  return {
    employeeContribution,
    employerContribution,
    totalContribution: employeeContribution + employerContribution
  };
}

/**
 * Calculate PAYE (Pay As You Earn) using progressive tax brackets
 */
export function calculatePaye(grossSalary: number): number {
  let totalTax = 0;
  let remainingIncome = grossSalary;

  for (const bracket of PAYE_BRACKETS) {
    if (remainingIncome <= bracket.min) break;

    const taxableInBracket = Math.min(remainingIncome - bracket.min, bracket.max - bracket.min);
    if (taxableInBracket > 0) {
      totalTax += taxableInBracket * bracket.rate;
    }
  }

  return Math.max(0, totalTax);
}

/**
 * Calculate Gross Salary from components
 */
export function calculateGrossSalary(components: SalaryComponents): number {
  return (
    components.baseSalary +
    components.housingAllowance +
    components.transportAllowance +
    components.responsibilityAllowance +
    components.otherAllowances
  );
}

/**
 * Calculate Total Deductions (non-statutory)
 */
export function calculateNonStatutoryDeductions(deductions: DeductionComponents): number {
  return (
    deductions.daysAbsentDeduction +
    deductions.advanceSalaryRepayment +
    deductions.loanRepayment +
    deductions.rentDeduction +
    deductions.schoolFeesDeduction +
    deductions.personalLevy +
    deductions.otherDeductions
  );
}

/**
 * Main Payroll Calculation Function
 * Computes all earnings, deductions, and net salary
 */
export function calculatePayroll(
  salaryComponents: SalaryComponents,
  deductionComponents: DeductionComponents,
  leaveDaysTaken: number = 0,
  leaveDaysEntitled: number = 30
): PayrollCalculationResult {
  // Calculate gross salary
  const grossSalary = calculateGrossSalary(salaryComponents);

  // Calculate statutory deductions
  const napsa = calculateNapsa(grossSalary);
  const napsaDeduction = deductionComponents.manualNapsa ?? napsa.employeeContribution;
  const payeDeduction = deductionComponents.manualPaye ?? calculatePaye(grossSalary);
  const nhima = calculateNhima(salaryComponents.baseSalary);
  const nhimaDeduction = deductionComponents.manualNhima ?? nhima.employeeContribution;

  // Calculate non-statutory deductions
  const nonStatutoryDeductions = calculateNonStatutoryDeductions(deductionComponents);

  // Total deductions
  const totalDeductions = (
    napsaDeduction +
    payeDeduction +
    nhimaDeduction +
    nonStatutoryDeductions
  );

  // Calculate net salary
  const netSalary = Math.max(0, grossSalary - totalDeductions);

  // Leave days remaining
  const leaveDaysRemaining = Math.max(0, leaveDaysEntitled - leaveDaysTaken);

  return {
    // Earnings
    baseSalary: salaryComponents.baseSalary,
    housingAllowance: salaryComponents.housingAllowance,
    transportAllowance: salaryComponents.transportAllowance,
    responsibilityAllowance: salaryComponents.responsibilityAllowance,
    otherAllowances: salaryComponents.otherAllowances,
    grossSalary,

    // Deductions
    daysAbsentDeduction: deductionComponents.daysAbsentDeduction,
    advanceSalaryRepayment: deductionComponents.advanceSalaryRepayment,
    loanRepayment: deductionComponents.loanRepayment,
    napsaDeduction,
    employeeNapsa: napsaDeduction,
    employerNapsa: napsa.employerContribution,
    totalNapsa: napsaDeduction + napsa.employerContribution,
    payeDeduction,
    nhimaDeduction,
    employeeNhima: nhimaDeduction,
    employerNhima: nhima.employerContribution,
    totalNhima: nhimaDeduction + nhima.employerContribution,
    personalLevy: deductionComponents.personalLevy,
    rentDeduction: deductionComponents.rentDeduction,
    schoolFeesDeduction: deductionComponents.schoolFeesDeduction,
    otherDeductions: deductionComponents.otherDeductions,
    totalDeductions,

    // Net
    netSalary,

    // Tax breakdown
    taxBreakdown: {
      employeeNapsa: napsaDeduction,
      employerNapsa: napsa.employerContribution,
      totalNapsa: napsaDeduction + napsa.employerContribution,
      paye: payeDeduction,
      employeeNhima: nhimaDeduction,
      employerNhima: nhima.employerContribution,
      totalNhima: nhimaDeduction + nhima.employerContribution,
      personalLevy: deductionComponents.personalLevy,
      totalStatutory: napsaDeduction + payeDeduction + nhimaDeduction + deductionComponents.personalLevy,
    },

    // Leave info
    leaveDaysTaken,
    leaveDaysRemaining,
  };
}

/**
 * Calculate daily rate for absent days deduction
 */
export function calculateDailyRate(monthlySalary: number, workingDays: number = 22): number {
  return monthlySalary / workingDays;
}

/**
 * Calculate deduction for absent days
 */
export function calculateAbsentDaysDeduction(
  monthlySalary: number,
  absentDays: number,
  workingDays: number = 22
): number {
  const dailyRate = calculateDailyRate(monthlySalary, workingDays);
  return dailyRate * absentDays;
}

/**
 * Format currency for display
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-ZM', {
    style: 'currency',
    currency: 'ZMW',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format number with thousands separator
 */
export function formatNumber(num: number): string {
  return new Intl.NumberFormat('en-US').format(num);
}

/**
 * Parse currency string to number
 */
export function parseCurrency(value: string): number {
  const cleaned = value.replace(/[^0-9.-]/g, '');
  return parseFloat(cleaned) || 0;
}

/**
 * Validate salary input
 */
export function validateSalary(amount: number): { valid: boolean; message?: string } {
  if (amount < 0) {
    return { valid: false, message: 'Salary cannot be negative' };
  }
  if (amount > 1000000) {
    return { valid: false, message: 'Salary amount seems unrealistic' };
  }
  return { valid: true };
}

/**
 * Generate payroll period name from date
 */
export function generatePeriodName(date: Date): string {
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
}

/**
 * Calculate employer NAPSA contribution
 */
export function calculateEmployerNapsa(grossSalary: number): number {
  const taxableAmount = Math.min(grossSalary, NAPSA_CONFIG.monthlyCeiling);
  return taxableAmount * NAPSA_CONFIG.employerRate;
}

/**
 * Get PAYE breakdown for display
 */
export function getPayeBreakdown(grossSalary: number): Array<{ bracket: string; amount: number; rate: number }> {
  const breakdown: Array<{ bracket: string; amount: number; rate: number }> = [];
  let remainingIncome = grossSalary;

  const bracketLabels = [
    'ZMW 0 - ZMW 5,100',
    'ZMW 5,101 - ZMW 7,100',
    'ZMW 7,101 - ZMW 9,200',
    'Above ZMW 9,200',
  ];

  for (let i = 0; i < PAYE_BRACKETS.length; i++) {
    if (remainingIncome <= PAYE_BRACKETS[i].min) break;

    const bracket = PAYE_BRACKETS[i];
    const taxableInBracket = Math.min(remainingIncome - bracket.min, bracket.max - bracket.min);

    if (taxableInBracket > 0) {
      breakdown.push({
        bracket: bracketLabels[i],
        amount: taxableInBracket * bracket.rate,
        rate: bracket.rate * 100,
      });
    }
  }

  return breakdown;
}

/**
 * Create empty salary components
 */
export function getEmptySalaryComponents(): SalaryComponents {
  return {
    baseSalary: 0,
    housingAllowance: 0,
    transportAllowance: 0,
    responsibilityAllowance: 0,
    otherAllowances: 0,
  };
}

/**
 * Create empty deduction components
 */
export function getEmptyDeductionComponents(): DeductionComponents {
  return {
    daysAbsentDeduction: 0,
    advanceSalaryRepayment: 0,
    loanRepayment: 0,
    rentDeduction: 0,
    schoolFeesDeduction: 0,
    personalLevy: 0,
    otherDeductions: 0,
  };
}

/**
 * Calculate total cost to employer
 */
export function calculateEmployerCost(grossSalary: number): {
  grossSalary: number;
  employerNapsa: number;
  totalEmployerCost: number;
} {
  const employerNapsa = calculateEmployerNapsa(grossSalary);
  return {
    grossSalary,
    employerNapsa,
    totalEmployerCost: grossSalary + employerNapsa,
  };
}

/**
 * Compare two payroll calculations for audit
 */
export function comparePayrollCalculations(
  before: PayrollCalculationResult,
  after: PayrollCalculationResult
): Array<{ field: string; before: number; after: number; difference: number }> {
  const changes: Array<{ field: string; before: number; after: number; difference: number }> = [];

  const beforeNum = before as unknown as Record<string, number>;
  const afterNum = after as unknown as Record<string, number>;

  const fieldsToCompare = [
    'baseSalary', 'housingAllowance', 'transportAllowance', 'responsibilityAllowance',
    'otherAllowances', 'grossSalary', 'napsaDeduction', 'payeDeduction', 'nhimaDeduction',
    'totalDeductions', 'netSalary'
  ];

  for (const field of fieldsToCompare) {
    const beforeVal = beforeNum[field] ?? 0;
    const afterVal = afterNum[field] ?? 0;
    const diff = afterVal - beforeVal;
    if (Math.abs(diff) > 0.01) {
      changes.push({
        field: field.replace(/([A-Z])/g, ' $1').trim(),
        before: beforeVal,
        after: afterVal,
        difference: diff,
      });
    }
  }

  return changes;
}
